import {
  Box,
  Container,
  Divider,
  Icon,
  Stack,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { ReactElement } from 'react';
import { useRecoilValue } from 'recoil';

import { DetailInfoCard } from '@app/components/Product/DetailInfoCard';
import { Loading } from '@app/components/Shared/Loading';
import { userAuthInfoSelector } from '@app/domain/app';
import {
  attributesByClinicalDepartmentSelector,
  attributesByJobTypeSelector,
} from '@app/domain/catalog';
import {
  Product,
  ProductCommuting,
  ProductCommutingKey,
  ProductCommutingLabel,
  ProductLocationType,
} from '@app/types/catalog';
import {
  getProductClinicalDeptLabel,
  getProductJobTypeLabel,
} from '@app/utils/catalog';
import { getConvertTimeToJp } from '@app/utils/date';

const commutingCostTarget: readonly ProductCommutingKey[] = [
  ProductCommuting.UPPER_LIMIT,
  ProductCommuting.FIXED_FEE,
  ProductCommuting.INCLUDE,
] as const;

interface ProductDetailInfoProps {
  product: Product;
}

export function ProductDetailInfo({
  product,
}: ProductDetailInfoProps): ReactElement {
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const attributesByClinicalDepartment = useRecoilValue(
    attributesByClinicalDepartmentSelector
  );
  const attributesByJobType = useRecoilValue(attributesByJobTypeSelector);

  const clinicalDeptLabel = getProductClinicalDeptLabel(
    attributesByClinicalDepartment,
    product
  );
  const jobTypeLabel = getProductJobTypeLabel(attributesByJobType, product);
  const workTimeLabel = [
    product?.customFields?.startTime?.substr(0, 5),
    '〜',
    getConvertTimeToJp(product?.customFields?.endTime || ''),
    product?.customFields?.overWorkConditions.length
      ? `\\n${product.customFields.overWorkConditions.join('、')}`
      : '',
  ]
    .filter((i) => i)
    .join(' ');

  const calculatedLabel = [
    product?.customFields?.calculatedDairyPay
      ? `${product.customFields.calculatedDairyPay.toLocaleString()}円`
      : '',
    product?.customFields?.calculatedHourlyPay
      ? `(時給${product.customFields.calculatedHourlyPay.toLocaleString()}円)`
      : '',
  ]
    .filter((i) => i)
    .join('');

  const commutingLabel = [
    ProductCommutingLabel(product?.customFields?.commuting || ''),
    product?.customFields?.commuting &&
    commutingCostTarget.includes(product?.customFields?.commuting)
      ? product?.customFields?.commutingCost
        ? ` ${product.customFields.commutingCost.toLocaleString()}円`
        : '（金額制限なし）'
      : '',
  ]
    .filter((i) => i)
    .join('');

  const addressFull = [
    product?.customFields?.workPostalCode
      ? `${product.customFields.workPostalCode}\\n`
      : '',
    product?.locations.find((l) => l.type === ProductLocationType.PREFECTURE)
      ?.name,
    product?.locations.find((l) => l.type === ProductLocationType.CITY)?.name,
    authInfo
      ? `${product?.customFields?.workAddress1 || ''}${
          product?.customFields?.workAddress2 || ''
        }`
      : '\\n（詳細は会員登録すると閲覧できます）',
  ]
    .filter((i) => i)
    .join('');

  const contentPart = (
    title: string,
    items: { label: string; value: string | undefined }[],
    icon: string
  ): ReactElement => {
    const data = items.filter((i) => i.value);
    return (
      <>
        {data.length > 0 && (
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon baseClassName="material-symbols-outlined">{icon}</Icon>
              <Typography fontWeight={500}>{title}</Typography>
            </Stack>
            <Divider sx={{ borderColor: 'text.primary' }} />
            <Stack spacing={2}>
              {data.map(({ label, value }, index) => (
                <Box key={index}>
                  <Typography fontWeight={500}>{label}</Typography>
                  {value &&
                    value.split('\\n').map((v, index) => (
                      <Typography key={index} fontWeight={300}>
                        {v}
                      </Typography>
                    ))}
                </Box>
              ))}
            </Stack>
          </Stack>
        )}
      </>
    );
  };

  return (
    <>
      {!product ? (
        <Loading />
      ) : (
        <Box>
          <DetailInfoCard product={product} />
          <Container sx={{ pb: 10, pt: 3 }}>
            <Stack spacing={3}>
              {contentPart(
                'インセンティブ・勤務条件',
                [
                  {
                    label: 'インセンティブ',
                    value: product.variants[0]?.description,
                  },
                  {
                    label: '勤務時間',
                    value: workTimeLabel,
                  },
                  {
                    label: '休憩時間',
                    value: !product.customFields?.breakTimeNoParticular
                      ? `${product.customFields.breakTime}分`
                      : '随時',
                  },
                  {
                    label: '日給',
                    value: calculatedLabel,
                  },
                  {
                    label: '交通費',
                    value: commutingLabel,
                  },
                ],
                'savings'
              )}
              {contentPart(
                '勤務内容',
                [
                  {
                    label: '募集科目',
                    value: clinicalDeptLabel,
                  },
                  {
                    label: '勤務内容',
                    value: jobTypeLabel,
                  },
                  {
                    label: '仕事内容',
                    value: product.description,
                  },
                ],
                'local_hospital'
              )}
              {contentPart(
                '他情報',
                [
                  {
                    label: '応募条件',
                    value: product.customFields?.orderConditions,
                  },
                  {
                    label: '選考方法',
                    value: product.customFields?.selection,
                  },
                  {
                    label: 'その他',
                    value: product.additionalInformation,
                  },
                ],
                'medical_information'
              )}
              {contentPart(
                '勤務場所',
                [
                  {
                    label: '病院名',
                    value: authInfo
                      ? _.get(product, 'organization.name')
                      : '（会員登録すると閲覧できます）',
                  },
                  {
                    label: '住所',
                    value: addressFull,
                  },
                  {
                    label: 'アクセス',
                    value: product.customFields?.access,
                  },
                ],
                'location_on'
              )}
            </Stack>
          </Container>
        </Box>
      )}
    </>
  );
}
